import { useCallback, useEffect, useMemo, useState } from 'react'

import { arrayMove } from 'react-sortable-hoc'

import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import { ISavedViewSettings } from '~/client/src/shared/enums/ProjectSpecificUserProps'
import UserProject from '~/client/src/shared/models/UserProject'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import LocalStorageHelper from '~/client/src/shared/utils/LocalStorageHelper'

import { DeleteFilterMutationFn, SaveFilterMutationFn } from './savedViewsTypes'
import {
  SavedView,
  ViewItem,
  getFilterIdField,
  transformViewsToItems,
  updateSavedViewSettings,
} from './savedViewsUtils'

const getDefaultRefetchQuery = (
  filterType: 'resources' | 'workflows' | 'uploadLog' | 'notifications',
): string => {
  switch (filterType) {
    case 'resources':
      return 'GetResourcesCustomFiltersByProject'
    case 'workflows':
      return 'GetWorkflowCustomFiltersByProject'
    case 'uploadLog':
      return 'GetUploadLogCustomFiltersByProject'
    case 'notifications':
      return 'GetNotificationCustomFiltersByProject'
  }
}

const getSaveFilterResultKey = (
  filterType: 'resources' | 'workflows' | 'uploadLog' | 'notifications',
): string => {
  switch (filterType) {
    case 'resources':
      return 'saveResourcesCustomFilter'
    case 'workflows':
      return 'saveWorkflowsCustomFilter'
    case 'uploadLog':
      return 'saveUploadLogCustomFilter'
    case 'notifications':
      return 'saveNotificationCustomFilter'
  }
}

const transformFiltersToApply = (
  filtersByFilterType:
    | Array<{ type?: string; values?: Array<string | null> }>
    | undefined,
): { [key: string]: Set<string> } => {
  const filtersToApply: { [key: string]: Set<string> } = {}
  filtersByFilterType?.forEach(filter => {
    if (filter?.type && filter?.values) {
      filtersToApply[filter.type] = new Set(
        filter.values.filter((v): v is string => v != null),
      )
    }
  })
  return filtersToApply
}

const buildUpdateFilterInput = (
  view: ViewItem,
  projectId: string,
  overrides: Partial<{
    name: string
    isPublic: boolean
  }>,
) => ({
  id: view.id,
  projectId,
  name: overrides.name ?? view.name,
  isPublic: overrides.isPublic ?? view.isPublic,
  filtersByFilterType: view.filtersByFilterType,
  groupBy: view.groupBy,
  userId: view.userId,
  defaultForUsers: [],
})

interface UseSavedViewsProps {
  views: SavedView[]
  filterType: 'resources' | 'workflows' | 'uploadLog' | 'notifications'
  userActiveProjectSettings: UserProject | undefined
  userProjectsStore: UserProjectsStore | undefined
  saveFilterMutation?: SaveFilterMutationFn
  deleteFilterMutation?: DeleteFilterMutationFn
  refetchQueryName?: string
  projectId: string
  userId: string
  isActiveUserAdmin: boolean
  appliedFilters: { [facetKey: string]: Set<string> }
  currentGrouping: string
  groupingOptions: IOptionItem[]
  onApplyFilters: (
    filters: { [key: string]: Set<string> },
    groupBy: string,
  ) => void
  localStorageKey?: LocalStorageKey
  projectCode?: string
  presetTabIds?: Set<string>
  defaultSelectedId?: string | null
}

export const useSavedViews = ({
  views,
  filterType,
  userActiveProjectSettings,
  userProjectsStore,
  saveFilterMutation,
  deleteFilterMutation,
  refetchQueryName,
  projectId,
  userId,
  isActiveUserAdmin,
  appliedFilters,
  currentGrouping,
  groupingOptions,
  onApplyFilters,
  localStorageKey,
  projectCode,
  presetTabIds,
  defaultSelectedId,
}: UseSavedViewsProps) => {
  const refetchQuery = refetchQueryName || getDefaultRefetchQuery(filterType)

  const [viewItems, setViewItems] = useState<ViewItem[]>([])
  const [selectedViewId, setSelectedViewId] = useState<string | null>(
    defaultSelectedId ?? null,
  )
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false)
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false)
  const [viewName, setViewName] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    const items = transformViewsToItems(
      views,
      userActiveProjectSettings?.savedViewsSettings,
      filterType,
    )
    setViewItems(items)
  }, [views, userActiveProjectSettings?.savedViewsSettings, filterType])

  const sortedVisibleViews = useMemo(
    () => viewItems.filter(v => !v.isHidden),
    [viewItems],
  )

  const allViewsForDialog = viewItems

  const groupByCaption = useMemo(() => {
    if (!currentGrouping) return ''
    const option = groupingOptions.find(opt => opt.key === currentGrouping)
    return option?.label || currentGrouping
  }, [currentGrouping, groupingOptions])

  const persistSettings = useCallback(
    (newItems: ViewItem[]) => {
      if (!userProjectsStore || !userActiveProjectSettings) {
        return
      }

      const filterIdField = getFilterIdField(filterType)
      const existingSettings =
        userActiveProjectSettings.savedViewsSettings || []

      const managedIds = new Set(newItems.map(item => item.id))
      const otherSettings = existingSettings.filter(
        setting =>
          setting[filterIdField] == null ||
          !managedIds.has(setting[filterIdField] as string),
      )

      const updatedSettings: ISavedViewSettings[] = newItems.map(item => ({
        [filterIdField]: item.id,
        isHidden: item.isHidden,
      }))

      const allSettings = [...otherSettings, ...updatedSettings]

      const userProjectDto = userActiveProjectSettings.toDto()
      userProjectDto.savedViewsSettings = allSettings

      userProjectsStore.save([userProjectDto])
    },
    [filterType, userActiveProjectSettings, userProjectsStore],
  )

  const persistViewSelection = useCallback(
    (viewId: string | null) => {
      if (localStorageKey && projectCode && userId) {
        const preferences =
          LocalStorageHelper.getUserPreferenceByKey(userId, localStorageKey) ||
          {}
        Object.assign(preferences, { [projectCode]: viewId })
        LocalStorageHelper.setUserPreferences(userId, {
          [localStorageKey]: preferences,
        })
      }
    },
    [localStorageKey, projectCode, userId],
  )

  const resetDialogState = useCallback(() => {
    setViewName('')
    setIsPublic(false)
    setSaveError(null)
  }, [])

  const handleViewSelect = useCallback(
    (viewId: string | null) => {
      setSelectedViewId(viewId)
      persistViewSelection(viewId)

      // Preset tabs handle their own filtering via onPresetTabSelect
      if (viewId && presetTabIds?.has(viewId)) {
        return
      }

      if (!viewId) {
        onApplyFilters({}, '')
        return
      }

      const view = viewItems.find(v => v.id === viewId)
      if (view) {
        const filtersToApply = transformFiltersToApply(view.filtersByFilterType)
        onApplyFilters(filtersToApply, view.groupBy || '')
      }
    },
    [viewItems, onApplyFilters, persistViewSelection, presetTabIds],
  )

  useEffect(() => {
    if (!localStorageKey || !projectCode || !userId || viewItems.length === 0) {
      return
    }

    const preferences = LocalStorageHelper.getUserPreferenceByKey(
      userId,
      localStorageKey,
    )
    const savedViewId = preferences?.[projectCode]

    if (savedViewId && viewItems.some(v => v.id === savedViewId)) {
      handleViewSelect(savedViewId)
    }
  }, [localStorageKey, projectCode, userId, viewItems, handleViewSelect])

  const handleAddClick = useCallback(() => {
    setIsNewDialogOpen(true)
    resetDialogState()
  }, [resetDialogState])

  const handleCreateView = useCallback(async () => {
    if (!viewName.trim()) {
      setSaveError('View name is required')
      return
    }

    setIsSaving(true)
    setSaveError(null)

    const filtersByFilterType = Object.entries(appliedFilters)
      .filter(([, valueSet]) => valueSet.size > 0)
      .map(([type, valueSet]) => ({
        type,
        values: Array.from(valueSet),
      }))

    const input = {
      name: viewName,
      isPublic,
      projectId,
      userId,
      filtersByFilterType,
      groupBy: currentGrouping,
      defaultForUsers: [],
    }

    try {
      if (!saveFilterMutation) {
        throw new Error('Save mutation not available')
      }

      const result = await saveFilterMutation({
        variables: { filter: input },
        refetchQueries: [refetchQuery],
      })
      const resultKey = getSaveFilterResultKey(filterType)
      const saved = result.data?.[resultKey]

      if (saved) {
        setSelectedViewId(saved.id)
        persistViewSelection(saved.id)
        setIsNewDialogOpen(false)
        resetDialogState()

        onApplyFilters(appliedFilters, currentGrouping)

        if (userProjectsStore && userActiveProjectSettings) {
          const filterIdField = getFilterIdField(filterType)
          updateSavedViewSettings(
            userProjectsStore,
            userActiveProjectSettings,
            filterType,
            settings => [
              ...settings,
              {
                [filterIdField]: saved.id,
                isHidden: false,
              },
            ],
          )
        }
      }
    } catch (error) {
      setSaveError(error.message || 'Failed to save view')
    } finally {
      setIsSaving(false)
    }
  }, [
    viewName,
    isPublic,
    projectId,
    appliedFilters,
    currentGrouping,
    saveFilterMutation,
    userProjectsStore,
    userActiveProjectSettings,
    filterType,
    persistViewSelection,
    resetDialogState,
    onApplyFilters,
    refetchQuery,
    userId,
  ])

  const handleCancelDialog = useCallback(() => {
    setIsNewDialogOpen(false)
    resetDialogState()
  }, [resetDialogState])

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setViewName(event.target.value)
    },
    [],
  )

  const handleTogglePublic = useCallback(() => {
    setIsPublic(prev => !prev)
  }, [])

  const handleManageDialogOpen = useCallback(() => {
    setIsManageDialogOpen(true)
  }, [])

  const handleManageDialogClose = useCallback(() => {
    setIsManageDialogOpen(false)
  }, [])

  const reorderViews = useCallback(
    (oldIndex: number, newIndex: number, targetSection: 'shown' | 'hidden') => {
      setViewItems(prev => {
        if (prev.length === 0) {
          return prev
        }

        let reorderedItems = arrayMove(prev, oldIndex, newIndex)

        reorderedItems = reorderedItems.map((item, index) => {
          if (index === newIndex) {
            return {
              ...item,
              isHidden: targetSection === 'hidden',
              order: index,
            }
          }
          return { ...item, order: index }
        })

        persistSettings(reorderedItems)

        return reorderedItems
      })
    },
    [persistSettings],
  )

  const toggleViewVisibility = useCallback(
    (savedViewId: string) => {
      setViewItems(prev => {
        const newItems = [...prev]
        const viewIndex = newItems.findIndex(item => item.id === savedViewId)
        if (viewIndex === -1) {
          return prev
        }

        const currentItem = newItems[viewIndex]
        const isCurrentlyHidden = currentItem.isHidden

        let firstHiddenIndex = newItems.findIndex(item => item.isHidden)
        if (firstHiddenIndex === -1) {
          firstHiddenIndex = newItems.length
        }

        const targetIndex = isCurrentlyHidden
          ? firstHiddenIndex - 1
          : firstHiddenIndex

        newItems[viewIndex] = {
          ...currentItem,
          isHidden: !isCurrentlyHidden,
        }

        if (viewIndex !== targetIndex) {
          const [movedItem] = newItems.splice(viewIndex, 1)
          newItems.splice(targetIndex, 0, movedItem)
        }

        const reorderedItems = newItems.map((item, index) => ({
          ...item,
          order: index,
        }))

        persistSettings(reorderedItems)

        return reorderedItems
      })
    },
    [persistSettings],
  )

  const renameView = useCallback(
    async (viewId: string, newName: string) => {
      if (!saveFilterMutation || !newName.trim()) {
        return
      }

      const view = viewItems.find(v => v.id === viewId)
      if (!view) {
        return
      }

      setViewItems(prev =>
        prev.map(item =>
          item.id === viewId ? { ...item, name: newName } : item,
        ),
      )

      try {
        await saveFilterMutation({
          variables: {
            filter: buildUpdateFilterInput(view, projectId, { name: newName }),
          },
        })
      } catch (error) {
        console.error('Failed to rename view:', error)
        throw error
      }
    },
    [saveFilterMutation, viewItems, projectId],
  )

  const deleteView = useCallback(
    async (viewId: string) => {
      if (!deleteFilterMutation) {
        return
      }

      try {
        await deleteFilterMutation({
          variables: { filterId: viewId },
          refetchQueries: [refetchQuery],
        })

        updateSavedViewSettings(
          userProjectsStore,
          userActiveProjectSettings,
          filterType,
          settings => {
            const filterIdField = getFilterIdField(filterType)
            return settings.filter(s => s[filterIdField] !== viewId)
          },
        )
      } catch (error) {
        console.error('Failed to delete view:', error)
        throw error
      }
    },
    [
      deleteFilterMutation,
      refetchQuery,
      userProjectsStore,
      userActiveProjectSettings,
      filterType,
    ],
  )

  const duplicateView = useCallback(
    async (viewId: string) => {
      if (!saveFilterMutation) {
        return
      }

      const viewToDuplicate = viewItems.find(v => v.id === viewId)
      if (!viewToDuplicate) {
        return
      }

      try {
        const duplicate = {
          projectId,
          userId,
          name: `${viewToDuplicate.name} (Copy)`,
          isPublic: false,
          filtersByFilterType: viewToDuplicate.filtersByFilterType,
          groupBy: viewToDuplicate.groupBy,
          defaultForUsers: [],
        }

        const result = await saveFilterMutation({
          variables: { filter: duplicate },
          refetchQueries: [refetchQuery],
        })

        const resultKey = getSaveFilterResultKey(filterType)
        if (result.data?.[resultKey]) {
          const newFilterId = result.data[resultKey].id

          setSelectedViewId(newFilterId)
          persistViewSelection(newFilterId)

          const filtersToApply = transformFiltersToApply(
            viewToDuplicate.filtersByFilterType,
          )
          onApplyFilters(filtersToApply, viewToDuplicate.groupBy || '')

          updateSavedViewSettings(
            userProjectsStore,
            userActiveProjectSettings,
            filterType,
            settings => {
              const filterIdField = getFilterIdField(filterType)
              return [
                ...settings,
                {
                  [filterIdField]: newFilterId,
                  isHidden: false,
                },
              ]
            },
          )
        }
      } catch (error) {
        console.error('Failed to duplicate view:', error)
        throw error
      }
    },
    [
      saveFilterMutation,
      viewItems,
      projectId,
      userId,
      userProjectsStore,
      userActiveProjectSettings,
      filterType,
      persistViewSelection,
      onApplyFilters,
      refetchQuery,
    ],
  )

  const updateViewAccess = useCallback(
    async (viewId: string) => {
      const view = viewItems.find(v => v.id === viewId)
      if (!view || !saveFilterMutation) {
        return
      }

      const newIsPublic = !view.isPublic

      setViewItems(prev =>
        prev.map(item =>
          item.id === viewId ? { ...item, isPublic: newIsPublic } : item,
        ),
      )

      try {
        await saveFilterMutation({
          variables: {
            filter: buildUpdateFilterInput(view, projectId, {
              isPublic: newIsPublic,
            }),
          },
        })
      } catch (error) {
        console.error('Failed to update view access:', error)
        throw error
      }
    },
    [viewItems, saveFilterMutation, projectId],
  )

  const handleReset = useCallback(() => {
    setSelectedViewId(null)
    persistViewSelection(null)
    onApplyFilters({}, '')
  }, [persistViewSelection, onApplyFilters])

  return {
    sortedVisibleViews,
    allViewsForDialog,
    selectedViewId,
    isNewDialogOpen,
    isManageDialogOpen,
    viewName,
    isPublic,
    groupByCaption,
    isSaving,
    saveError,
    isActiveUserAdmin,
    handleViewSelect,
    handleAddClick,
    handleCreateView,
    handleCancelDialog,
    handleNameChange,
    handleTogglePublic,
    handleManageDialogOpen,
    handleManageDialogClose,
    reorderViews,
    toggleViewVisibility,
    renameView,
    deleteView,
    duplicateView,
    updateViewAccess,
    handleReset,
  }
}
